<script setup lang="ts">
import { api } from '@/api';
import type { CreateInitialAdminUserDto } from '@/api/generator';
import { CreateInitialAdminUserDtoTypeEnum } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { success as successIcon } from '@/components/icons/icons';
import Notification from '@/components/notifications/Notification.vue';
import PasswordQualityEstimation from '@/components/PasswordQualityEstimation/PasswordQualityEstimation.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useZxcvbn } from '@/plugins/zxcvbn';
import { routenames } from '@/router/routenames';
import { truthy } from '@/utils/helper';
import {
  type CompletedEventArgs,
  type CompletedStep,
  completedStepsOrdered,
} from '@/views/InstallationView/types';
import { useFieldValue } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import InstallationStep from './InstallationStep.vue';

const router = useRouter();

interface InstallationUserAccountProps {
  completedStep: CompletedStep;
}
const props = defineProps<InstallationUserAccountProps>();

interface CreateInitialAdminUserDtoEmailRepeated extends CreateInitialAdminUserDto {
  emailRepeated: string;
}

const emit = defineEmits<{
  completed: [args: CompletedEventArgs];
}>();

const showSuccessMessage = ref(false);
const isAdminAccountAlreadyCreated = computed(
  () => completedStepsOrdered.indexOf(props.completedStep) === 4,
);

const { form: userAccountForm } = useVeeForm<CreateInitialAdminUserDtoEmailRepeated>({
  options: {
    initialValues: {
      surname: '',
      email: '',
      emailRepeated: '', // additional property, see CreateInitialAdminUserDto in @/validation/patches/patches.ts
      $type:
        CreateInitialAdminUserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedInstallDtoCreateInitialAdminUserDtoPtwPtw100AppZeroApplicationShared,
    },
  },
  schemaName: 'CreateInitialAdminUserDto',
  mode: 'edit',
});
const { handleSubmit: handleSubmitUserAccountForm, values, resetForm } = userAccountForm;

const password = useFieldValue<string>('password');

const userInputs = computed(() => [values.email, values.surname].filter(truthy));

const zxcvbnResult = useZxcvbn(password, userInputs);

const onSubmit = handleSubmitUserAccountForm(async (createInitialAdminUserDto) => {
  const { success } = await api.installApi.createAdminUser(
    {
      createInitialAdminUserDto,
    },
    [formBackendErrorHandler(userAccountForm)],
  );
  if (success) {
    showSuccessMessage.value = true;
    // after completing the user account creation,
    // the user needs to be able to navigate to
    // the sign-in page which would be prevented
    // if the form is still dirty.
    resetForm();
    emit('completed', { shouldUpdate: true, preventNextNavigation: true });
  }
});
</script>

<template>
  <form @submit="onSubmit">
    <InstallationStep>
      <template #content>
        <h4 class="mb-4">{{ $t('CreateAdminAccount') }}</h4>
        <p>{{ $t('UserAccountMsg') }}</p>
        <Notification
          class="w-lg-50 my-4"
          :value="{
            type: 'info',
            closable: false,
            message: $t('ChangesOnlyInPlatformSettingsMsg'),
          }"
        />

        <v-row class="mt-8">
          <v-col cols="12" lg="6">
            <VeeTextField
              name="surname"
              :label="$t('LastName')"
              :placeholder="$t('Required')"
              :disabled="isAdminAccountAlreadyCreated"
            />
            <VeeTextField
              name="email"
              :label="$t('Email')"
              :placeholder="$t('Required')"
              :disabled="isAdminAccountAlreadyCreated"
            />
            <VeeTextField
              name="emailRepeated"
              :label="$t('EmailRepeated')"
              :placeholder="$t('Required')"
              :disabled="isAdminAccountAlreadyCreated"
            />
          </v-col>
          <v-col cols="12" lg="6">
            <VeePasswordField
              name="password"
              :label="$t('Password')"
              :placeholder="$t('Required')"
              :hint="$t('PasswordComplexityViolationMsg')"
              :persistentHint="true"
              :disabled="isAdminAccountAlreadyCreated"
            />
            <PasswordQualityEstimation :result="zxcvbnResult" />
          </v-col>
        </v-row>
      </template>
      <template #footer>
        <v-alert v-if="showSuccessMessage" class="mt-8 fit-content" type="success" :icon="false">
          <template #text>
            <v-icon class="mr-2" :icon="successIcon"></v-icon>
            <span class="font-weight-semibold">{{ $t('AdminAccountAdded') }}</span>
          </template>
        </v-alert>
        <VSpacer />
        <VeeSubmitButton class="mt-8" :text="$t('Save')" :disabled="isAdminAccountAlreadyCreated" />
        <Button
          variant="secondary"
          class="mt-8 ml-2"
          :label="$t('GoToSignIn')"
          :disabled="completedStepsOrdered.indexOf(completedStep) !== 4"
          @click="router.push({ name: routenames.signIn })"
        />
      </template>
    </InstallationStep>
  </form>
</template>
